<template>
  <div class="editor-bundles-container">
    <div class="header">
      <h2>Editor Bundles</h2>
      <div class="header-actions">
        <el-button type="primary" size="small" @click="openCreateDialog">Create Bundle</el-button>
        <el-button size="small" @click="fetchData">Refresh</el-button>
      </div>
    </div>

    <el-table
      v-loading="loading"
      :data="items"
      border
      size="small"
      style="width: 100%"
    >
      <el-table-column prop="display_rank" label="Rank" width="80" />
      <el-table-column prop="title" label="Title" min-width="200">
        <template slot-scope="scope">
          <div style="font-weight: 600">{{ scope.row.title }}</div>
          <div v-if="scope.row.subtitle" style="font-size: 12px; color: #909399">{{ scope.row.subtitle }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Items" width="90">
        <template slot-scope="scope">
          {{ (scope.row.items || []).length }}
        </template>
      </el-table-column>
      <el-table-column label="Status" width="110">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 'PUBLISHED' ? 'success' : 'info'" size="small">
            {{ scope.row.status || 'DRAFT' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Updated" width="160">
        <template slot-scope="scope">
          {{ formatDate(scope.row.update_time) }}
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="200" fixed="right">
        <template slot-scope="scope">
          <el-button type="primary" size="mini" @click="openEditDialog(scope.row)">Edit</el-button>
          <el-button type="danger" size="mini" @click="handleDelete(scope.row)">Delete</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Create / Edit dialog -->
    <el-dialog
      :title="editingId ? 'Edit Bundle' : 'Create Bundle'"
      :visible.sync="showDialog"
      width="800px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="120px" size="small">
        <el-form-item label="Title" required>
          <el-input v-model="form.title" placeholder="Bundle title" />
        </el-form-item>
        <el-form-item label="Subtitle">
          <el-input v-model="form.subtitle" placeholder="Optional short tagline" />
        </el-form-item>
        <el-form-item label="Description">
          <el-input
            type="textarea"
            :rows="3"
            v-model="form.description"
            placeholder="Editor intro (markdown supported)"
          />
        </el-form-item>
        <el-form-item label="Cover URL">
          <el-input v-model="form.cover_url" placeholder="https://..." />
        </el-form-item>
        <el-form-item label="Display Rank">
          <el-input-number v-model="form.display_rank" :min="0" :max="9999" />
          <span style="margin-left: 10px; font-size: 12px; color: #909399">Lower = earlier</span>
        </el-form-item>
        <el-form-item label="Status">
          <el-radio-group v-model="form.status">
            <el-radio label="DRAFT">Draft</el-radio>
            <el-radio label="PUBLISHED">Published</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="Collections">
          <div class="collection-search">
            <el-input
              v-model="searchQuery"
              placeholder="Search collections by title..."
              size="small"
              clearable
              @keyup.enter.native="searchCollections"
              @clear="searchCollections"
              style="width: 300px"
            />
            <el-button size="small" type="primary" @click="searchCollections" style="margin-left: 8px">Search</el-button>
          </div>

          <div v-if="searchResults.length" class="search-results">
            <div
              v-for="c in searchResults"
              :key="c._id"
              class="result-item"
              @click="addCollection(c)"
            >
              <img v-if="c.cover_url" :src="c.cover_url" class="thumb" />
              <div v-else class="thumb placeholder"></div>
              <div class="meta">
                <div class="title">{{ c.title }}</div>
                <div class="sub">{{ c.user_name }}</div>
              </div>
              <el-button size="mini" type="text" icon="el-icon-plus">Add</el-button>
            </div>
          </div>

          <div class="selected-items" v-if="form.items && form.items.length">
            <div class="selected-header">Selected ({{ form.items.length }})</div>
            <draggable-substitute v-if="false" />
            <div
              v-for="(item, idx) in form.items"
              :key="idx"
              class="selected-item"
            >
              <div class="order-controls">
                <el-button size="mini" icon="el-icon-arrow-up" :disabled="idx === 0" @click="moveItem(idx, -1)" />
                <el-button size="mini" icon="el-icon-arrow-down" :disabled="idx === form.items.length - 1" @click="moveItem(idx, 1)" />
              </div>
              <img v-if="item._cover" :src="item._cover" class="thumb" />
              <div v-else class="thumb placeholder"></div>
              <div class="meta">
                <div class="title">{{ item._title || item.collection_id }}</div>
                <el-input
                  v-model="item.editor_comment"
                  size="mini"
                  type="textarea"
                  :rows="2"
                  placeholder="Editor comment (optional)"
                  style="margin-top: 6px"
                />
              </div>
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeItem(idx)" />
            </div>
          </div>
          <div v-else class="empty-items">No collections added yet.</div>
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="showDialog = false">Cancel</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">
          {{ editingId ? 'Save' : 'Create' }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  listEditorBundles,
  getEditorBundle,
  createEditorBundle,
  updateEditorBundle,
  deleteEditorBundle,
  searchCollectionsForAdmin
} from '@/apis/lfAdmin'
import dayjs from 'dayjs'

export default {
  name: 'EditorBundles',
  data() {
    return {
      loading: false,
      submitting: false,
      items: [],
      showDialog: false,
      editingId: null,
      form: this.emptyForm(),
      searchQuery: '',
      searchResults: []
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    emptyForm() {
      return {
        title: '',
        subtitle: '',
        description: '',
        cover_url: '',
        display_rank: 0,
        status: 'DRAFT',
        items: []
      }
    },
    formatDate(ts) {
      if (!ts) return ''
      return dayjs.unix(ts).format('YYYY-MM-DD HH:mm')
    },
    async fetchData() {
      this.loading = true
      try {
        const res = await listEditorBundles({ include_draft: 1 })
        if (res.data.code === 0) {
          this.items = res.data.data || []
        } else {
          this.$message.error(res.data.msg || 'Failed to load')
        }
      } catch (e) {
        this.$message.error('Failed to load')
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    openCreateDialog() {
      this.editingId = null
      this.form = this.emptyForm()
      this.searchQuery = ''
      this.searchResults = []
      this.showDialog = true
    },
    async openEditDialog(row) {
      this.editingId = this._extractId(row)
      this.searchQuery = ''
      this.searchResults = []
      try {
        const res = await getEditorBundle({ id: this.editingId })
        if (res.data.code === 0) {
          const b = res.data.data
          const hydrated = b.items_hydrated || []
          const rawItems = b.items || []
          // Map raw items to enriched form items, preferring hydrated data when present.
          const enrichedItems = rawItems.map((raw, i) => {
            const h = hydrated[i]
            const col = h && h.collection ? h.collection : null
            return {
              collection_id: raw.collection_id,
              editor_comment: raw.editor_comment || '',
              _title: col ? col.title : '',
              _cover: col ? col.cover_url : ''
            }
          })
          this.form = {
            title: b.title || '',
            subtitle: b.subtitle || '',
            description: b.description || '',
            cover_url: b.cover_url || '',
            display_rank: b.display_rank || 0,
            status: b.status || 'DRAFT',
            items: enrichedItems
          }
          this.showDialog = true
        } else {
          this.$message.error(res.data.msg || 'Failed to load bundle')
        }
      } catch (e) {
        this.$message.error('Failed to load bundle')
        console.error(e)
      }
    },
    async handleDelete(row) {
      const id = this._extractId(row)
      try {
        await this.$confirm(`Delete bundle "${row.title}"?`, 'Confirm', { type: 'warning' })
      } catch { return }
      try {
        const res = await deleteEditorBundle({ id })
        if (res.data.code === 0) {
          this.$message.success('Deleted')
          this.fetchData()
        } else {
          this.$message.error(res.data.msg || 'Failed to delete')
        }
      } catch (e) {
        this.$message.error('Failed to delete')
      }
    },
    async searchCollections() {
      try {
        const res = await searchCollectionsForAdmin({ q: this.searchQuery, limit: 20 })
        if (res.data.code === 0) {
          this.searchResults = res.data.data || []
        } else {
          this.$message.error(res.data.msg || 'Search failed')
        }
      } catch (e) {
        this.$message.error('Search failed')
      }
    },
    addCollection(c) {
      const cid = this._extractId(c)
      if (!cid) return
      if (this.form.items.some((i) => i.collection_id === cid)) {
        this.$message.info('Already added')
        return
      }
      this.form.items.push({
        collection_id: cid,
        editor_comment: '',
        _title: c.title,
        _cover: c.cover_url
      })
    },
    removeItem(idx) {
      this.form.items.splice(idx, 1)
    },
    moveItem(idx, delta) {
      const newIdx = idx + delta
      if (newIdx < 0 || newIdx >= this.form.items.length) return
      const [item] = this.form.items.splice(idx, 1)
      this.form.items.splice(newIdx, 0, item)
    },
    async submit() {
      if (!this.form.title.trim()) {
        this.$message.error('Title is required')
        return
      }
      const payload = {
        title: this.form.title.trim(),
        subtitle: this.form.subtitle,
        description: this.form.description,
        cover_url: this.form.cover_url,
        display_rank: this.form.display_rank,
        status: this.form.status,
        items: this.form.items.map((i) => ({
          collection_id: i.collection_id,
          editor_comment: i.editor_comment || ''
        }))
      }
      this.submitting = true
      try {
        const fn = this.editingId ? updateEditorBundle : createEditorBundle
        const body = this.editingId ? { id: this.editingId, ...payload } : payload
        const res = await fn(body)
        if (res.data.code === 0) {
          this.$message.success(this.editingId ? 'Saved' : 'Created')
          this.showDialog = false
          this.fetchData()
        } else {
          this.$message.error(res.data.msg || 'Failed')
        }
      } catch (e) {
        this.$message.error('Failed')
      } finally {
        this.submitting = false
      }
    },
    _extractId(obj) {
      if (!obj) return ''
      if (typeof obj === 'string') return obj
      const id = obj._id
      if (!id) return ''
      if (typeof id === 'string') return id
      return id.$oid || id.$id || (id.toString ? id.toString() : '')
    }
  }
}
</script>

<style scoped>
.editor-bundles-container {
  padding: 20px;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.header-actions {
  display: flex;
  gap: 8px;
}
.collection-search {
  margin-bottom: 10px;
}
.search-results {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-bottom: 12px;
}
.result-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-bottom: 1px solid #f2f3f5;
  cursor: pointer;
}

.result-item:last-child {
  border-bottom: none;
}

.result-item:hover {
  background: #f5f7fa;
}
.thumb {
  width: 40px;
  height: 56px;
  object-fit: cover;
  border-radius: 3px;
  background: #f2f3f5;
  flex-shrink: 0;
}

.thumb.placeholder {
  background: linear-gradient(135deg, #f2f3f5, #e4e7ed);
}
.meta {
  flex: 1;
  min-width: 0;
}

.meta .title {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta .sub {
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}
.selected-items {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px;
  background: #fafbfc;
}
.selected-header {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
}
.selected-item {
  display: flex;
  gap: 10px;
  padding: 8px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-bottom: 8px;
}

.selected-item:last-child {
  margin-bottom: 0;
}
.order-controls {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.empty-items {
  font-size: 12px;
  color: #909399;
  padding: 10px;
  text-align: center;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
}
</style>
